import { DimSumEnhancedNode } from '@amzn/gft-dimsum-service';
import { InputProps } from '@mui/material';
import { KeyboardEvent } from 'react';

/**
 * The ref handle for the CalculatedKPIBuilder component.
 */
export interface CalculatedKPIBuilderHandle {
  /**
   * Used for injecting a DimSumEnhancedNode into the Formula.
   * The node will be inserted into the formula based on the current internal input focus position.
   * @param node The DimSumEnhancedNode to insert.
   */
  insertNodeIntoFormula: (node: DimSumEnhancedNode) => void;

  /**
   * Forces resolution of any node segments the customer typed but that have not yet been resolved to a
   * `DimSumEnhancedNode` (the background resolution is debounced, so a fast confirm can race it).
   * Resolves them synchronously, commits the result to the builder, and returns the resolved segments
   * so the caller can serialize `calc_members` with every node's hierarchy present.
   * @returns the builder's segments with pending typed nodes resolved where a match was found.
   */
  resolvePendingNodes: () => Promise<KpiFormulaNode[]>;
}

/**
 * Props for the CalculatedKPIBuilder component.
 */
export interface CalculatedKPIBuilderProps {
  /**
   * The dimension to use for validating and autocompleting tokens/codes.
   */
  dimensionName?: string;
  // initialValue?: string; // TODO: Work-in-progress

  /**
   * Callback used when the formula changes.
   * @param formula
   * @param isValid
   * @param validationError - If the formula is invalid, this will contain the error message. Otherwise, it will be null.
   * @param segments - The builder's current resolved segments (nodes carry their `dimSumNode`), so a
   *  caller can read each node's hierarchy for `calc_members` without re-resolving.
   */
  onFormulaChange?: (
    formula: string,
    isValid: boolean,
    validationError: string | null,
    segments?: KpiFormulaNode[],
  ) => void;

  /**
   * The visible label to use for the component.
   */
  label?: string;

  initialFormula?: string;

  /**
   * Debug mode, when true, renders additional information beneath the input field.
   */
  debug?: boolean;
}

/**
 * Defines the type of KPI formula segment.
 * @enum {string}
 */
export enum KpiFormulaNodeType {
  // The segment is a supported Math Operator (+, -, *, /, etc.)
  Operator = 'operator',
  // The segment is linked to a DimSum Node
  Node = 'node',
  // Unknown/Undetermined. Typically seen on blank segments (awaiting customer input).
  Unknown = 'unknown',
}

/**
 * Object state of a KPI formula segment.
 */
export interface KpiFormulaNode {
  value: string;
  type: KpiFormulaNodeType;
  dimSumNode?: DimSumEnhancedNode;
}

/**
 * Enum for distinguishing the intended direction of the input caret movement.
 * These are used by the parent component to determine any input field focus changes caused by keyboard navigation.
 */
export enum CaretPositionChangeType {
  /**
   * Movement to the immediate previous field
   */
  Previous = -1,
  /**
   * Movement to the immediate next field
   */
  Next = 1,
  /**
   * Movement to the beginning of the input field list (i.e.: HOME key presses)
   */
  Beginning = -9999,
  /**
   * Movement to the end of the input field list (i.e.: END key presses)
   */
  End = 9999,
}

/**
 * Props for the KpiSegmentInput component.
 */
export interface KpiSegmentInputProps extends Omit<InputProps, 'onKeyDown'> {
  /**
   * The index number of this input field within the list of input fields from the parent component.
   */
  segmentIndex: number;
  /**
   * The current input field value.
   */
  value?: string;
  /**
   * The current dimSumNode associated with this input field.
   */
  dimSumNode?: DimSumEnhancedNode;
  /**
   * The ref callback to pass to the internal MUI <Input> component.
   * @param instance
   */
  inputRef?: (instance: HTMLInputElement | null) => void;

  /**
   * Callback function to be called when the caret position needs to move to the next input.
   * For example: if the user presses the "left" arrow key when the caret position is already at the beginning of this
   * input field, this callback will be called with the direction "previous".
   * @param segmentIndex - The index number of the input field that is requesting the caret position change
   * @param direction - The direction of the caret position change
   * @param deleteKey - Whether the key pressed was the "delete" key
   */
  onCaretPositionChangeRequest?: (
    segmentIndex: number,
    direction: CaretPositionChangeType,
    deleteKey?: boolean,
  ) => void;

  /**
   * Callback for when a user chooses to insert a new KPISegmentInput after this one, typically from a keyboard shortcut
   * (i.e.: SHIFT+ENTER)
   * @param segmentIndex
   */
  onAddSegmentRequest?: (segmentIndex: number) => void;

  /**
   * Callback for when this input field gains focus.
   * @param segmentIndex
   */
  onFocusChange?: (segmentIndex: number) => void;

  /**
   * Callback for when this input field loses focus.
   * @param segmentIndex
   */
  handleBlur?: (segmentIndex: number) => void;

  /**
   * Callback for when the value of this input field changes.
   * @param segmentIndex
   * @param newValue
   */
  onValueChange?: (segmentIndex: number, newValue: NonNullable<KpiSegmentInputProps['value']>) => void;

  /**
   * Generic callback for when a keyboard event occurs that is not otherwise handled by any of the other callbacks.
   * @param segmentIndex
   * @param event
   */
  onKeyDown?: (segmentIndex: number, event: KeyboardEvent<HTMLInputElement>) => void;

  /**
   * Callback for when the user detaches this input field from the KPI segment builder.
   * This typically occurs when the user clicks on the "Dismiss" icon of a rendered Chip.
   * @param segmentIndex
   */
  handleDetachNode?: (segmentIndex: number) => void;
}
